import { appNoOp } from '@actions/app.action';
import { bookingTripDetailsInit } from '@actions/booking.action';
import {
  SmsVerificationActionTypes,
  smsVerificationGetBookingVerificationStatus,
  smsVerificationGetBookingVerificationStatusSuccess,
  smsVerificationVerifyBookingChange,
  smsVerificationVerifyBookingChangeComplete,
  smsVerificationVerifyBookingChangeCompleteSuccess,
  smsVerificationVerifyBookingChangeResponse,
} from '@actions/sms-verification.action';
import { UserActionTypes, userLoginSuccess } from '@actions/user.action';
import { Injectable } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {selectAppCmsCopy, selectSkybidMMBHomePageV6Copy} from '@selectors/cms.selector';
import { selectUrl } from '@selectors/router.selector';
import { VerificationService } from '@services/api-clients/verification.service';
import { ModalService } from '@services/modal/modal.service';
import { map, mergeMap, of, switchMap } from 'rxjs';
import {GrowthBookService} from "@services/growthbook/growthbook.service";

@Injectable()
export class SmsVerificationEffect {
  constructor(
    private actions$: Actions,
    private verificationService: VerificationService,
    private modalService: ModalService,
    private store: Store,
    private gbs: GrowthBookService,
  ) {}

  verifyBookingChange$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(smsVerificationVerifyBookingChange),
      mergeMap((action) => {
        const isSkybidV6Enabled = this.gbs.isVariantOfExperimentEnabled('feat-003128-skybid-mmb-home-page-revamp-v6', 'b');

        return this.verificationService
          .initBookingVerification(action.request)
          .pipe(
            concatLatestFrom(() => [this.store.select(selectAppCmsCopy), this.store.select(selectSkybidMMBHomePageV6Copy)]),
            switchMap(([{ data: response }, copy, skybidCopy]) => {
              if (!response.verify) {
                return of(appNoOp());
              }

              import(
                '../../../components/modals/sms-verify-modal/sms-verify-modal.component'
              ).then(({ SmsVerifyModalComponent: SmsVerifyModalComponent }) => {
                if (!copy || !skybidCopy) {
                  return;
                }
                this.modalService.show(SmsVerifyModalComponent, {
                  class: isSkybidV6Enabled ? 'modal-desktop-600' : '',
                  initialState: {
                    nextAction:
                      SmsVerificationActionTypes.verifyBookingChangeComplete,
                    mfa: response,
                    copy,
                    isSkybidV6Enabled,
                    skybidV6Copy: skybidCopy
                  },
                });
              });
              return of(
                smsVerificationVerifyBookingChangeResponse({ response }),
              );
            }),
          );
      }),
    );
  });

  verifyBookingChangeComplete$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(smsVerificationVerifyBookingChangeComplete),
      mergeMap((action) => {
        return this.verificationService
          .completeBookingVerification(action.payload)
          .pipe(
            switchMap(({ data: response }) => {
              return of(
                smsVerificationVerifyBookingChangeCompleteSuccess({ response }),
              );
            }),
          );
      }),
    );
  });

  getBookingVerificationStatus$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        smsVerificationGetBookingVerificationStatus,
        bookingTripDetailsInit,
        userLoginSuccess,
      ),
      concatLatestFrom(() => [this.store.select(selectUrl)]),
      mergeMap(([action, url]) => {
        if (
          action.type === UserActionTypes.loginSuccess &&
          !url.includes('/trip-details')
        ) {
          return of(appNoOp());
        }

        return this.verificationService.getBookingVerificationStatus().pipe(
          map((response) => {
            return smsVerificationGetBookingVerificationStatusSuccess({
              status: response.data.status,
            });
          }),
        );
      }),
    );
  });
}
